import { mkConfig, generateCsv, asBlob } from 'export-to-csv'
import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'

import { ISODateStringToYYYYMMDD } from '@/utils/util'
import { fetchAuthenticatedData } from '@/queries/shared'
import { DownloadBlob } from '@/components/components'
import { EffectiveDateRecord, LeaDataModel, defaultLeaData, SCLR, MutationResponse } from '@/models'
import { FileStatusEnum, FileSubmission } from '@/models/file-submission.tsx'

export type SASS = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    LocalStaffId: string
    ClassroomRole1: string
    ClassroomRole1PercentOfTime: number
    ClassroomRole2: string
    ClassroomRole2PercentOfTime: number
    NonClassroomRole1: string
    NonClassroomRole1PercentOfTime: number
    NonClassroomRole2: string
    NonClassroomRole2PercentOfTime: number
    NonClassroomRole3: string
    NonClassroomRole3PercentOfTime: number
}

export const SASS_TEMPLATE : SASS = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    LocalStaffId: '',
    ClassroomRole1: '',
    ClassroomRole1PercentOfTime: 0,
    ClassroomRole2: '',
    ClassroomRole2PercentOfTime: 0,
    NonClassroomRole1: '',
    NonClassroomRole1PercentOfTime: 0,
    NonClassroomRole2: '',
    NonClassroomRole2PercentOfTime: 0,
    NonClassroomRole3: '',
    NonClassroomRole3PercentOfTime: 0,
}


export type STFF = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    StaffLegalFirstName: string
    StaffLegalMiddleName: string
    StaffLegalLastName: string
    SEID: string
    LocalStaffId: string
    StaffPermitOrCredentialNumber: string
    StaffPermitOrCredentialType: string
    StaffPermitIssuedDate: string
    StaffPermitExpirationDate: string
    StaffWaiver: string
    StaffWaiverType: string
    StaffWaiverIssuedDate: string
    StaffWaiverExpirationDate: string
    StaffDateOfBirth: string
    StaffGender: string
    StaffEthnicity: string
    StaffEthnicityMissingIndicator: string
    StaffRace1: string
    StaffRace2: string
    StaffRace3: string
    StaffRace4: string
    StaffRace5: string
    StaffRaceMissingIndicator: string
    StaffHighestDegreeCode: string
    StaffLanguage1: string
    StaffLanguage2: string
    StaffLanguage3: string
    StaffLanguage4: string
    StaffLanguage5: string
    StaffCurrentEmploymentStartDate: string
    StaffCurrentEmploymentEndDate: string
    StaffOverallEmploymentStartDate: string
    StaffAssignments: Array<SASS> | undefined
    ClassroomAssignments: Array<SCLR> | undefined
} & EffectiveDateRecord

export function STAFF_Key(x: STFF): string {
  return x.LocalStaffId;
}

export const STFF_TEMPLATE: STFF = {
    Id: '',
    ReportingLEA: '',
    ProgramYear: '',
    StaffLegalFirstName: '',
    StaffLegalMiddleName: '',
    StaffLegalLastName: '',
    StaffDateOfBirth: '',
    SEID: '',
    LocalStaffId: '',
    StaffGender: '',
    StaffEthnicity: '',
    StaffEthnicityMissingIndicator: '',
    StaffRace1: '',
    StaffRace2: '',
    StaffRace3: '',
    StaffRace4: '',
    StaffRace5: '',
    StaffRaceMissingIndicator: '',
    StaffLanguage1: '',
    StaffLanguage2: '',
    StaffLanguage3: '',
    StaffLanguage4: '',
    StaffLanguage5: '',
    StaffHighestDegreeCode: '',
    StaffWaiver: '',
    StaffWaiverType: '',
    StaffWaiverIssuedDate: '',
    StaffWaiverExpirationDate: '',
    StaffPermitOrCredentialNumber: '',
    StaffPermitOrCredentialType: '',
    StaffPermitIssuedDate: '',
    StaffPermitExpirationDate: '',
    StaffCurrentEmploymentStartDate: '',
    StaffCurrentEmploymentEndDate: '',
    StaffOverallEmploymentStartDate: '',
    EffectiveStartDate: '',
    EffectiveEndDate: '',

    StaffAssignments: undefined,
    ClassroomAssignments: undefined,
}

export function updateSTFF(s: STFF, hasRole: (...roles: string[]) => boolean | undefined, lea_data: LeaDataModel): STFF {
    s.StaffAssignments = !hasRole('SASSView') ? undefined : lea_data.sass.filter((e) => e.LocalStaffId === s.LocalStaffId)
    s.ClassroomAssignments = !hasRole('SCLRView') ? undefined : lea_data.sclr.filter((e) => e.LocalStaffId === s.LocalStaffId)
    return s
}

export function asSerializeable(staff: STFF) {
    return {
        Id: staff.Id,
        ReportingLEA: staff.ReportingLEA,
        ProgramYear: staff.ProgramYear,
        StaffLegalFirstName: staff.StaffLegalFirstName,
        StaffLegalMiddleName: staff.StaffLegalMiddleName,
        StaffLegalLastName: staff.StaffLegalLastName,
        SEID: staff.SEID,
        LocalStaffId: staff.LocalStaffId,
        StaffDateOfBirth: staff.StaffDateOfBirth,
        StaffGender: staff.StaffGender,
        StaffEthnicity: (staff.StaffEthnicity === '') ? '' : (staff.StaffEthnicity === 'Y'),
        StaffEthnicityMissingIndicator: (staff.StaffEthnicityMissingIndicator === 'Y'),
        StaffRace1: staff.StaffRace1,
        StaffRace2: staff.StaffRace2,
        StaffRace3: staff.StaffRace3,
        StaffRace4: staff.StaffRace4,
        StaffRace5: staff.StaffRace5,
        StaffRaceMissingIndicator: (staff.StaffRaceMissingIndicator === 'Y'),
        StaffLanguage1: staff.StaffLanguage1,
        StaffLanguage2: staff.StaffLanguage2,
        StaffLanguage3: staff.StaffLanguage3,
        StaffLanguage4: staff.StaffLanguage4,
        StaffLanguage5: staff.StaffLanguage5,
        StaffHighestDegreeCode: staff.StaffHighestDegreeCode,
        StaffWaiver: (staff.StaffWaiver === 'Y'),
        StaffWaiverType: staff.StaffWaiverType,
        StaffWaiverIssuedDate: staff.StaffWaiverIssuedDate,
        StaffWaiverExpirationDate: staff.StaffWaiverExpirationDate,
        StaffPermitOrCredentialNumber: staff.StaffPermitOrCredentialNumber,
        StaffPermitOrCredentialType: staff.StaffPermitOrCredentialType,
        StaffPermitIssuedDate: staff.StaffPermitIssuedDate,
        StaffPermitExpirationDate: staff.StaffPermitExpirationDate,
        StaffCurrentEmploymentStartDate: staff.StaffCurrentEmploymentStartDate,
        StaffCurrentEmploymentEndDate: staff.StaffCurrentEmploymentEndDate,
        StaffOverallEmploymentStartDate: staff.StaffOverallEmploymentStartDate,
        EffectiveStartDate: staff.EffectiveStartDate,
    }
}

export function asSerializeableSASS(sass: SASS) {
    return {
        Id: sass.Id,
        ReportingLEA: sass.ReportingLEA,
        PreschoolCode: sass.PreschoolCode,
        ProgramYear: sass.ProgramYear,
        LocalStaffId: sass.LocalStaffId,
        ClassroomRole1: sass.ClassroomRole1,
        ClassroomRole1PercentOfTime: sass.ClassroomRole1PercentOfTime == 0 ? "" : sass.ClassroomRole1PercentOfTime,
        ClassroomRole2: sass.ClassroomRole2,
        ClassroomRole2PercentOfTime: sass.ClassroomRole2PercentOfTime == 0 ? "" : sass.ClassroomRole2PercentOfTime,
        NonClassroomRole1: sass.NonClassroomRole1,
        NonClassroomRole1PercentOfTime: sass.NonClassroomRole1PercentOfTime == 0 ? "" : sass.NonClassroomRole1PercentOfTime,
        NonClassroomRole2: sass.NonClassroomRole2,
        NonClassroomRole2PercentOfTime: sass.NonClassroomRole2PercentOfTime == 0 ? "" : sass.NonClassroomRole2PercentOfTime,
        NonClassroomRole3: sass.NonClassroomRole3,
        NonClassroomRole3PercentOfTime: sass.NonClassroomRole3PercentOfTime == 0 ? "" : sass.NonClassroomRole3PercentOfTime,
    }
}

export function asSerializeableSCLR(sclr: SCLR) {
    return {
        Id: sclr.Id,
        ReportingLEA: sclr.ReportingLEA,
        PreschoolCode: sclr.PreschoolCode,
        ProgramYear: sclr.ProgramYear,
        LocalStaffId: sclr.LocalStaffId,
        LocalClassroomId: sclr.LocalClassroomId,
    }
}

export function mutationFunctions(leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<STFF>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const mutation = useMutation({
        mutationFn: ({ urlVal, staff }: { urlVal: string, staff: STFF }) => {
            return fetchAuthenticatedData<MutationResponse<STFF>>(leaContext, urlVal, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(asSerializeable(staff))
            }).then(resp => {
                setSubmitResult(resp)
            })
        },
        onSuccess: (data) => {
        },
        onError: (error) => {
            console.error(error)
        },
    })
    return {
        mutationState: mutation,
        handleSave: async function (staff: STFF, isDelete: boolean = false) {
            var urlVal = `/${leaContext.currentUserOrg.esiIdPath}/dashboard/${isDelete ? 'D' : 'U'}/ajax/EditStaffAjax`
            mutation.mutate({ urlVal, staff })
        }
    }
}

export function sassMutationFunctions(leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<SASS>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const mutation = useMutation({
        mutationFn: ({ urlVal, sass }: { urlVal: string, sass: SASS }) => {
            return fetchAuthenticatedData<MutationResponse<SASS>>(leaContext, urlVal, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(asSerializeableSASS(sass))
            }).then(resp => {
                setSubmitResult(resp)
            })
        },
        onSuccess: (data) => {
        },
        onError: (error) => {
            console.error(error)
        },
    })
    return {
        mutationState: mutation,
        handleSave: async function (sass: SASS, isDelete: boolean = false) {
            var urlVal = `/${leaContext.currentUserOrg.esiIdPath}/dashboard/${isDelete ? 'D' : 'U'}/ajax/EditSASSAjax`
            mutation.mutate({ urlVal, sass })
        }
    }
}

export function sclrMutationFunctions(leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<SCLR>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const mutation = useMutation({
        mutationFn: ({ urlVal, sclr }: { urlVal: string, sclr: SCLR }) => {
            return fetchAuthenticatedData<MutationResponse<SCLR>>(leaContext, urlVal, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(asSerializeableSCLR(sclr))
            }).then(resp => {
                setSubmitResult(resp)
            })
        },
        onSuccess: (data) => {
        },
        onError: (error) => {
            console.error(error)
        },
    })
    return {
        mutationState: mutation,
        handleSave: async function (sclr: SCLR, isDelete: boolean = false) {
            var urlVal = `/${leaContext.currentUserOrg.esiIdPath}/dashboard/${isDelete ? 'D' : 'U'}/ajax/EditSCLRAjax`
            mutation.mutate({ urlVal, sclr })
        }
    }
}

